import { KeySequence } from "@nimit9/signet-ui/components/kbd"
import { useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { cn } from "@/lib/cn"

export const SHORTCUT_GROUPS = [
  {
    label: "Navigation",
    shortcuts: [
      { keys: ["g", "d"], label: "Dashboard" },
      { keys: ["g", "t"], label: "Transactions" },
      { keys: ["g", "a"], label: "Analytics" },
      { keys: ["g", "b"], label: "Budgets" },
      { keys: ["g", "g"], label: "Goals" },
      { keys: ["g", "n"], label: "Net Worth" },
      { keys: ["g", "i"], label: "Investments" },
      { keys: ["g", "r"], label: "Reports" },
      { keys: ["g", "u"], label: "Upcoming" },
      { keys: ["g", "c"], label: "Calculators" },
      { keys: ["g", "s"], label: "Settings" },
    ],
  },
  {
    label: "Global",
    shortcuts: [
      { keys: ["⌘", "K"], label: "Command palette" },
      { keys: ["?"], label: "Show shortcuts" },
      { keys: ["h"], label: "Hide / show amounts" },
      { keys: ["q"], label: "Quick add (desktop)" },
      { keys: ["Esc"], label: "Close panel / dialog" },
    ],
  },
  {
    label: "Transactions",
    shortcuts: [
      { keys: ["/"], label: "Focus search" },
      { keys: ["j"], label: "Next transaction" },
      { keys: ["k"], label: "Previous transaction" },
      { keys: ["⌘", "U"], label: "Upload statement" },
      { keys: ["n"], label: "New transaction" },
      { keys: ["↵"], label: "Open focused transaction" },
      { keys: ["e"], label: "Edit focused transaction" },
      { keys: ["i"], label: "Ignore / un-ignore focused" },
      { keys: ["x"], label: "Select / deselect row" },
      { keys: ["⌘", "A"], label: "Select all on this page" },
      { keys: ["Home"], label: "First row" },
      { keys: ["⇧", "G"], label: "Last row" },
      { keys: ["1–6"], label: "All / Needs / Wants / Invest / Income / Transfer" },
      { keys: ["→"], label: "Next page" },
      { keys: ["←"], label: "Previous page" },
    ],
  },
] as const

interface ShortcutsHelpProps {
  open: boolean
  onClose: () => void
}

export function ShortcutsHelp({ open, onClose }: ShortcutsHelpProps) {
  useHotkeys("escape", onClose, { enabled: open })

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl border border-border-default bg-bg-base p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary">Keyboard shortcuts</h2>
          <KeySequence keys={["Esc"]} />
        </div>
        <div className="grid grid-cols-2 gap-6">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.label} className={group.shortcuts.length > 5 ? "col-span-2" : ""}>
              <p className="mb-2 text-xs font-medium text-text-muted">{group.label}</p>
              <div
                className={cn(
                  "space-y-1.5",
                  group.shortcuts.length > 5 && "grid grid-cols-2 gap-x-6 space-y-0 [&>*]:mb-1.5",
                )}
              >
                {group.shortcuts.map(({ keys, label }) => (
                  <div key={label} className="flex items-center justify-between gap-4">
                    <span className="text-xs text-text-secondary">{label}</span>
                    <KeySequence keys={Array.isArray(keys) ? [...keys] : [keys]} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function useShortcutsHelp() {
  const [open, setOpen] = useState(false)
  useHotkeys("?", () => setOpen(true), { preventDefault: true })
  return { open, close: () => setOpen(false) }
}
